import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Panel, Chip } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, cur, cc } from "@/lib/api";

const STRATEGY = [
  ["sma_period_bars", "SMA period (bars)", "5 to 200"], ["stddev_mult", "Std dev multiplier", "0.5 to 5"],
  ["bar_interval_sec", "Bar interval (s)", "30 to 3600"], ["tick_interval_sec", "Sample interval (s)", "15 to 600"],
  ["demo_balance", "Demo account balance (£)", ""], ["risk_pct", "Risk %", "0.01 to 10"],
  ["ig_spread_cost_pts", "Spread cost (pts)", ""], ["manual_stake_per_point", "Manual stake £/pt", "0.5 to 1000, blank = calculator"],
  ["points_per_unit", "Points per unit", ""], ["take_profit_points", "Take profit (pts)", ""],
  ["entry_open_london", "Entries open (London)", "HH:MM"], ["entry_cutoff_london", "Entry cut-off", "HH:MM"],
  ["force_close_at_london", "Force close at", "HH:MM"],
];
const STRAT_BOOLS = [["take_profit_enabled", "Fixed take profit"], ["flip_enabled", "Flip on exit (always in market)"], ["trading_hours_enabled", "Trading hours"], ["weekend_trading_enabled", "Weekend trading"]];
const GATE = [
  ["gate_z_entry", "Entry |z|"], ["gate_z_exit", "Exit |z|"], ["gate_z_stop", "Stop |z|"], ["gate_z_rearm", "Re-arm |z|"],
  ["gate_cooldown_min", "Cooldown (min)"], ["gate_min_hold_min", "Min hold (min)"], ["gate_max_hold_hours", "Max hold (h)"],
  ["gate_round_trip_cost_gbp", "Round trip cost (£)"], ["gate_min_edge_multiple", "Min edge multiple"],
];
const TEXT = new Set(["entry_open_london", "entry_cutoff_london", "force_close_at_london"]);

function useForm(cfg, keys) {
  const [f, setF] = useState({});
  useEffect(() => {
    const o = {};
    keys.forEach((k) => { o[k] = cfg[k] ?? ""; });
    setF(o);
  }, [cfg]); // eslint-disable-line react-hooks/exhaustive-deps
  return [f, setF];
}

async function save(f, onSaved) {
  const body = {};
  Object.entries(f).forEach(([k, v]) => {
    body[k] = typeof v === "boolean" || TEXT.has(k) ? v : v === "" ? null : Number(v);
  });
  try { await api.put("/oil/config", body); toast.success("Saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
}

const NumField = ({ k, label, hint, f, setF }) => (
  <label className="space-y-1 text-xs text-slate-400">
    <span>{cc(label)}</span>
    <Input data-testid={`oil-cfg-${k}`} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="num h-9 bg-slate-950" />
    {hint && <span className="block text-[10px] text-slate-400">{hint}</span>}
  </label>
);

export function StrategyConfig({ cfg, onSaved }) {
  const [f, setF] = useForm(cfg, [...STRATEGY.map((x) => x[0]), ...STRAT_BOOLS.map((x) => x[0])]);
  return (
    <Panel title="Strategy config" testid="oil-strategy-config">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{STRATEGY.map(([k, l, h]) => <NumField key={k} k={k} label={l} hint={k === "demo_balance" && cfg.balance_from_mt5 ? "Live on: sizing uses MT5 balance" : h} f={f} setF={setF} />)}</div>
      <div className="mt-4 flex flex-wrap gap-5">
        {STRAT_BOOLS.map(([k, l]) => <label key={k} className="flex items-center gap-2 text-sm text-slate-300"><Switch data-testid={`oil-cfg-${k}`} checked={!!f[k]} onCheckedChange={(v) => setF({ ...f, [k]: v })} />{l}</label>)}
      </div>
      <Button data-testid="oil-strategy-save" className="mt-4 btn-gold" onClick={() => save(f, onSaved)}>Save strategy</Button>
    </Panel>
  );
}

export function GateConfig({ cfg, gate, onSaved }) {
  const [f, setF] = useForm(cfg, ["gate_enabled", ...GATE.map((x) => x[0])]);
  return (
    <Panel title="Advanced gate (hysteresis and cooldown)" testid="oil-gate-card" right={<Chip status={!cfg.gate_enabled ? "OFF" : gate?.state === "cooldown" ? "WARNING" : gate?.state === "in_position" ? "IN_INITIAL_TRADE" : "ON"} label={cfg.gate_enabled ? gate?.state : "off"} testid="oil-gate-state" />}>
      <label className="mb-4 flex items-center gap-2 text-sm text-slate-300"><Switch data-testid="oil-cfg-gate_enabled" checked={!!f.gate_enabled} onCheckedChange={(v) => setF({ ...f, gate_enabled: v })} />Use the gate instead of plain band signals</label>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">{GATE.map(([k, l]) => <NumField key={k} k={k} label={l} f={f} setF={setF} />)}</div>
      <p className="mt-3 text-xs text-slate-500">Armed, in position, cooldown, re-arm. An entry needs |z| x σ in {cur()} at the calculated stake to be at least the edge multiple times the round trip cost.</p>
      <Button data-testid="oil-gate-save" className="mt-3 btn-gold" onClick={() => save(f, onSaved)}>Save gate</Button>
    </Panel>
  );
}
